import { useCallback, useState } from 'react';

import { TextInput } from '@/components/common/TextInput';
import type { Me } from '@/hooks/useMe';
import { type UpdateMeInput, useUpdateMe } from '@/hooks/useUpdateMe';

interface ProfileEditFormProps {
  me: Me;
  refetch: () => Promise<void>;
  onDone: () => void;
}

export function ProfileEditForm({ me, refetch, onDone }: ProfileEditFormProps) {
  const { updateMe, isSubmitting, error } = useUpdateMe();
  const [name, setName] = useState(me.name);
  const [surname, setSurname] = useState(me.surname);
  const [localError, setLocalError] = useState<string | null>(null);

  const trimmedName = name.trim();
  const trimmedSurname = surname.trim();
  const hasDiff = trimmedName !== me.name || trimmedSurname !== me.surname;
  const isValid = trimmedName.length > 0 && trimmedSurname.length > 0;

  const handleSave = useCallback(async () => {
    if (!isValid) {
      setLocalError('Name and surname cannot be empty.');
      return;
    }
    if (!hasDiff) {
      onDone();
      return;
    }
    setLocalError(null);
    const patch: UpdateMeInput = {};
    if (trimmedName !== me.name) patch.name = trimmedName;
    if (trimmedSurname !== me.surname) patch.surname = trimmedSurname;
    const result = await updateMe(patch);
    if (result) {
      await refetch();
      onDone();
    }
  }, [hasDiff, isValid, me.name, me.surname, onDone, refetch, trimmedName, trimmedSurname, updateMe]);

  const errorMessage = localError ?? error?.message ?? null;

  return (
    <form
      data-testid="account-profile-edit-form"
      className="w-full flex flex-col gap-3 mt-4"
      onSubmit={(e) => {
        e.preventDefault();
        void handleSave();
      }}
    >
      <label className="flex flex-col gap-1 text-sm text-black/70">
        First name
        <TextInput
          value={name}
          onChange={setName}
          disabled={isSubmitting}
          fullWidth
          testId="account-edit-name"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm text-black/70">
        Last name
        <TextInput
          value={surname}
          onChange={setSurname}
          disabled={isSubmitting}
          fullWidth
          testId="account-edit-surname"
        />
      </label>
      {errorMessage !== null && (
        <p
          role="alert"
          className="text-wolisho-red text-sm"
          data-testid="account-edit-error"
        >
          {errorMessage}
        </p>
      )}
      <div className="flex gap-2 justify-center pt-2">
        <button
          type="submit"
          disabled={isSubmitting || !isValid}
          data-testid="account-edit-save"
          className="rounded-lg bg-wolisho-red px-5 py-2 font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 hover:bg-wolisho-red/90"
        >
          Save
        </button>
        <button
          type="button"
          onClick={onDone}
          disabled={isSubmitting}
          data-testid="account-edit-cancel"
          className="rounded-lg border border-wolisho-brown/30 px-5 py-2 font-semibold text-wolisho-brown transition-colors disabled:cursor-not-allowed disabled:opacity-60 hover:border-wolisho-red"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
